import { type Handle, css } from 'remix/component'
import { orgAccountApiReferrer } from '#client/org-account-api.ts'
import { readCurrentRouterHref } from '#client/client-router.tsx'
import { createRouteData, routeDataRedirect } from '#client/route-data.tsx'
import {
	type AccountStatus,
	readJson,
} from '#client/routes/account-approval-shared.ts'
import { createAccountConnectedAgents } from '#client/routes/account-connected-agents-panel.tsx'
import {
	ConnectionUsageDetails,
	ConnectionUsageSummaryPanel,
	fetchConnectionUsage,
	type ConnectionUsageState,
} from '#client/routes/account-connection-usage-panel.tsx'
import {
	AccountManagementMessage,
	AccountManagementPanel,
	AccountManagementShell,
	AccountPageHeader,
	accountActionsCss,
} from '#client/routes/account-management-components.tsx'
import { connectedAgentsApiPath } from '#client/routes/account-page-data.ts'
import { CopyCard } from '#client/routes/onboarding-mcp-client-cards.tsx'
import {
	AgentPickerGrid,
	AgentSurfaceInstructions,
} from '#client/routes/onboarding-mcp-client-tabs.tsx'
import { createConnectionProfiles } from '#client/routes/connection-profiles-panel.tsx'
import {
	routeLoaderRedirect,
	type RouteLoaderResult,
} from '#client/route-loader.ts'
import {
	type AccountConnectionsView,
	accountConnectionAgentIds,
	accountConnectionsListHref,
	accountConnectionsNewHref,
	parseAccountConnectionsPathname,
} from '#universal/account-connections.ts'
import { docHref } from '#universal/docs-nav.ts'
import { orgSlugFromOrgOwnedPath } from '#universal/org-pages.ts'
import { type AccountConnectedAgentsLoaderData } from '#universal/loader-data.ts'
import { onboardingSecondAgentGreyedPresentation } from '#universal/onboarding-agent-ecosystems.ts'
import {
	type McpClientKind,
	onboardingAgentLabel,
} from '#universal/onboarding-mcp-clients.ts'
import { routes } from '#universal/routes.ts'
import {
	getGhostButtonCss,
	getPillButtonCss,
} from '#universal/styles/style-primitives.ts'
import { colors, spacing, typography } from '#universal/styles/tokens.ts'

const connectYourAgentDocHref = docHref('connect-your-agent')

async function fetchConnectedAgents(href: string, signal: AbortSignal) {
	const response = await fetch(connectedAgentsApiPath, {
		headers: { Accept: 'application/json' },
		credentials: 'include',
		referrer: orgAccountApiReferrer(href),
		signal,
	})
	if (response.status === 401) return { kind: 'unauthorized' as const }
	const payload = await readJson<AccountConnectedAgentsLoaderData>(response)
	if (!response.ok || !payload?.ok) {
		throw new Error('Unable to load connections.')
	}
	return { kind: 'ok' as const, payload }
}

export async function accountConnectionsRouteLoader(
	url: URL,
	signal: AbortSignal,
): Promise<RouteLoaderResult> {
	const [result, usage] = await Promise.all([
		fetchConnectedAgents(url.href, signal),
		viewShowsUsage(readView(url.href))
			? loadUsageWithinBudget(url.href, signal)
			: null,
	])
	if (result.kind === 'unauthorized') {
		return routeLoaderRedirect('/login')
	}
	return {
		accountConnectedAgents: result.payload,
		...(usage?.kind === 'ok' ? { accountConnectionUsage: usage.payload } : {}),
	}
}

/**
 * Usage is a secondary panel. The loader waits for it only briefly so a slow
 * usage request never holds the Connections navigation; after a miss or a
 * failure the route data fallback fetches it once the page commits.
 */
const loaderUsageBudgetMs = 1500

async function loadUsageWithinBudget(href: string, signal: AbortSignal) {
	const controller = new AbortController()
	const abort = () => controller.abort()
	signal.addEventListener('abort', abort, { once: true })
	const timer = setTimeout(abort, loaderUsageBudgetMs)
	try {
		return await fetchConnectionUsage(href, controller.signal)
	} catch {
		return null
	} finally {
		clearTimeout(timer)
		signal.removeEventListener('abort', abort)
	}
}

function viewShowsUsage(view: AccountConnectionsView | null) {
	return view?.kind === 'list' || view?.kind === 'usage'
}

/**
 * Every view reads one usage payload per organization; only the list and
 * usage views ask.
 */
function connectionUsageLatchKey(href: string) {
	return `${connectionsLatchKey(href)}/usage`
}

function readUsageFocus(href: string) {
	return (
		new URL(href, 'http://localhost').searchParams.get('connection')?.trim() ||
		null
	)
}

/**
 * The list, grid, and per-agent views all read one connected-agents payload.
 * Each organization lists its own agents, so switching orgs is a new load.
 */
function connectionsLatchKey(href: string) {
	const slug = orgSlugFromOrgOwnedPath(
		new URL(href, 'http://localhost').pathname,
	)
	return slug ? `/@${slug}/-/connections` : '/account/connections'
}

function readView(href: string): AccountConnectionsView | null {
	return parseAccountConnectionsPathname(
		new URL(href, 'http://localhost').pathname,
	)
}

/**
 * `/account/connections` — the durable home for inbound MCP hosts. The
 * connected list is the same panel Overview used to host. Add connection
 * is its own page (`/new`): the full client wall from onboarding Step 1
 * with no phone/desktop split and nothing folded under Not listed, not a
 * nested child of the list. `/new/:agent` shows that host's install
 * steps. Sign-in providers stay on Overview; outbound MCP servers stay on
 * MCP servers.
 */
export function AccountConnectionsRoute(handle: Handle) {
	const connectedAgents = createAccountConnectedAgents(handle)
	let mcpServerUrl = ''
	let message: string | null = null
	const connectionProfiles = createConnectionProfiles(handle, {
		onSaved: applyPayload,
	})
	/** Payload last applied to the closure state above. */
	let appliedPayload: AccountConnectedAgentsLoaderData | null = null
	let appliedError: Error | null = null
	const connectionsData = createRouteData({
		key: 'accountConnectedAgents',
		locationKey: connectionsLatchKey,
		async load(href, signal) {
			const result = await fetchConnectedAgents(href, signal)
			if (result.kind === 'unauthorized') return routeDataRedirect('/login')
			return result.payload
		},
	})

	const usageData = createRouteData({
		key: 'accountConnectionUsage',
		locationKey: connectionUsageLatchKey,
		async load(href, signal) {
			const result = await fetchConnectionUsage(href, signal)
			if (result.kind === 'unauthorized') return routeDataRedirect('/login')
			return result.payload
		},
	})

	function readUsageState(currentHref: string): ConnectionUsageState {
		const usage = usageData.read(handle, currentHref)
		// Stale data belongs to another org (the latch key is per org).
		if (usage.data && !usage.stale) return { kind: 'ready', usage: usage.data }
		if (usage.kind === 'error') {
			return {
				kind: 'error',
				message: usage.error?.message ?? 'Unable to load connection usage.',
			}
		}
		return { kind: 'loading' }
	}

	function applyPayload(payload: AccountConnectedAgentsLoaderData) {
		connectedAgents.applyPayload(payload)
		mcpServerUrl = payload.mcpServerUrl
		connectionProfiles.applyPayload(payload)
		message = null
	}

	return () => {
		const currentHref = readCurrentRouterHref(handle)
		const view = readView(currentHref)
		const snapshot = connectionsData.read(handle, currentHref)
		if (snapshot.data && snapshot.data !== appliedPayload) {
			appliedPayload = snapshot.data
			applyPayload(snapshot.data)
		}
		if (snapshot.error && snapshot.error !== appliedError) {
			appliedError = snapshot.error
			message = snapshot.error.message
		}
		const usage = viewShowsUsage(view) ? readUsageState(currentHref) : null
		const pending = snapshot.kind === 'pending'
		const status: AccountStatus =
			snapshot.kind === 'error'
				? 'error'
				: pending && appliedPayload === null
					? 'loading'
					: 'ready'

		return (
			<AccountManagementShell busy={pending && appliedPayload !== null}>
				<AccountPageHeader
					title="Connections"
					description="The agents connected to this Kody account, and how to connect another."
					currentHref={currentHref}
				/>

				{status === 'loading' ? (
					<p mix={css({ color: colors.textMuted, margin: 0 })}>
						Loading connections…
					</p>
				) : null}
				{message ? (
					<AccountManagementMessage tone="error">
						{message}
					</AccountManagementMessage>
				) : null}

				{status === 'ready'
					? renderReadyView({
							view,
							connectedAgents,
							mcpServerUrl,
							connectionProfiles,
							usage,
							usageFocus: readUsageFocus(currentHref),
							pathname: new URL(currentHref, 'http://localhost').pathname,
						})
					: null}
			</AccountManagementShell>
		)
	}
}

function renderBackToConnections(pathname: string) {
	return (
		<a
			href={accountConnectionsListHref(pathname)}
			data-testid="account-connections-back"
			mix={css(backLinkCss)}
		>
			← back to connections
		</a>
	)
}

function renderReadyView(input: {
	view: AccountConnectionsView | null
	connectedAgents: ReturnType<typeof createAccountConnectedAgents>
	mcpServerUrl: string
	connectionProfiles: ReturnType<typeof createConnectionProfiles>
	usage: ConnectionUsageState | null
	usageFocus: string | null
	pathname: string
}) {
	if (input.view === null) {
		return (
			<>
				{renderBackToConnections(input.pathname)}
				{renderUnknownAgent(input.pathname)}
			</>
		)
	}
	switch (input.view.kind) {
		case 'list':
			return (
				<>
					{input.connectedAgents.render({
						pathname: input.pathname,
						actions: (
							<a
								href={accountConnectionsNewHref(null, input.pathname)}
								data-testid="account-connections-add"
								mix={css(primaryButtonCss)}
							>
								Add connection
							</a>
						),
					})}
					{input.usage ? (
						<ConnectionUsageSummaryPanel
							state={input.usage}
							agents={input.connectedAgents.listAgents()}
							pathname={input.pathname}
						/>
					) : null}
					{renderMcpUrlPanel({ mcpServerUrl: input.mcpServerUrl })}
					{input.connectionProfiles.render({
						agents: input.connectedAgents.listAgents(),
					})}
					{renderAdvancedPanel()}
				</>
			)
		case 'new':
			return (
				<>
					{renderBackToConnections(input.pathname)}
					{input.view.agent === null
						? renderAgentGrid({
								mcpServerUrl: input.mcpServerUrl,
								connectedAgents: input.connectedAgents.listAgents(),
								pathname: input.pathname,
							})
						: renderAgentInstructions({
								agent: input.view.agent,
								mcpServerUrl: input.mcpServerUrl,
								pathname: input.pathname,
							})}
				</>
			)
		case 'usage':
			return (
				<>
					{renderBackToConnections(input.pathname)}
					<ConnectionUsageDetails
						state={input.usage ?? { kind: 'loading' }}
						agents={input.connectedAgents.listAgents()}
						pathname={input.pathname}
						focus={input.usageFocus}
					/>
				</>
			)
		default: {
			const _exhaustive: never = input.view
			return _exhaustive
		}
	}
}

function renderAdvancedPanel() {
	return (
		<AccountManagementPanel
			title="Advanced"
			description="Optional tools for hosts that cannot finish dynamic OAuth on their own. This is not the list of agents already connected to your account."
		>
			<div mix={css(accountActionsCss)}>
				<a
					href={routes.accountMcpOauthClients.href()}
					mix={css(compactGhostButtonCss)}
				>
					MCP OAuth clients
				</a>
			</div>
		</AccountManagementPanel>
	)
}

function renderVerifyNote() {
	return (
		<p
			data-testid="account-connections-verify-note"
			mix={css({ color: colors.textMuted, margin: 0 })}
		>
			Verify your email to get this deployment&apos;s MCP URL. MCP access stays
			locked until the account email is verified.{' '}
			<a href={routes.pendingVerification.href()}>Verification page</a>
		</p>
	)
}

function renderMcpUrlPanel(input: { mcpServerUrl: string }) {
	return (
		<AccountManagementPanel
			title="MCP URL"
			description="Every host reaches the same account through one MCP URL. Add connection walks through a specific agent; any other agent that speaks MCP can paste this URL and approve the Kody OAuth window."
			ariaLabel="MCP URL"
		>
			{input.mcpServerUrl ? (
				<CopyCard
					label="MCP URL"
					value={input.mcpServerUrl}
					copyLabel="Copy MCP URL"
					variant="pill"
				/>
			) : (
				renderVerifyNote()
			)}
			<div mix={css(accountActionsCss)}>
				<a href={connectYourAgentDocHref} mix={css(compactGhostButtonCss)}>
					Connect your agent docs
				</a>
			</div>
		</AccountManagementPanel>
	)
}

function renderAgentGrid(input: {
	mcpServerUrl: string
	connectedAgents: AccountConnectedAgentsLoaderData['agents']
	pathname: string
}) {
	const connectedMark = onboardingSecondAgentGreyedPresentation(
		input.connectedAgents,
	)
	return (
		<AccountManagementPanel
			title="Add connection"
			description="Pick the agent you want to connect — including ones already connected, when you need the steps again for a second login, new machine, or reinstall. Every agent Kody knows how to connect is listed here, on every device; the next step shows that host's install path."
			ariaLabel="Add connection"
		>
			{input.mcpServerUrl ? (
				<div
					data-testid="account-connections-agent-grid"
					mix={css({ display: 'grid', gap: spacing.lg })}
				>
					<span class="visually-hidden" id="account-connections-add-title">
						Agents you can connect
					</span>
					<AgentPickerGrid
						ids={accountConnectionAgentIds.map((id) => ({
							id,
							viewport: 'both' as const,
						}))}
						labelledBy="account-connections-add-title"
						agentHref={(agent) =>
							accountConnectionsNewHref(agent, input.pathname)
						}
						greyedAgents={connectedMark.greyedAgents}
						greyedReasons={connectedMark.greyedReasons}
						greyedTitles={connectedMark.greyedTitles}
					/>
					<div mix={css({ display: 'grid', gap: spacing.sm })}>
						<p mix={css({ color: colors.textMuted, margin: 0 })}>
							Using something else? Any agent that speaks MCP connects with this
							URL. Approve the Kody OAuth window when the host opens it.
						</p>
						<CopyCard
							label="MCP URL"
							value={input.mcpServerUrl}
							copyLabel="Copy MCP URL"
						/>
					</div>
				</div>
			) : (
				renderVerifyNote()
			)}
		</AccountManagementPanel>
	)
}

function renderAgentInstructions(input: {
	agent: McpClientKind
	mcpServerUrl: string
	pathname: string
}) {
	const label = onboardingAgentLabel(input.agent)
	return (
		<AccountManagementPanel
			title={`Connect ${label}`}
			description="Follow the steps for this host, then approve the Kody OAuth window when it opens. The new connection appears on the connections list once the host authorizes."
			ariaLabel={`Connect ${label}`}
		>
			<div mix={css(accountActionsCss)}>
				<a
					href={accountConnectionsNewHref(null, input.pathname)}
					data-testid="account-connections-change-agent"
					mix={css(compactGhostButtonCss)}
				>
					Change agent
				</a>
			</div>
			{input.mcpServerUrl ? (
				<div
					data-testid="account-connections-agent-instructions"
					data-agent={input.agent}
					mix={css({ display: 'grid', gap: spacing.lg })}
				>
					<AgentSurfaceInstructions
						agent={input.agent}
						mcpServerUrl={input.mcpServerUrl}
					/>
				</div>
			) : (
				renderVerifyNote()
			)}
		</AccountManagementPanel>
	)
}

function renderUnknownAgent(pathname: string) {
	return (
		<AccountManagementPanel
			title="Unknown agent"
			description="That agent is not one Kody knows how to connect. Pick one from the list, or use the MCP URL with any host that speaks MCP."
		>
			<div mix={css(accountActionsCss)}>
				<a
					href={accountConnectionsNewHref(null, pathname)}
					mix={css(compactGhostButtonCss)}
				>
					Choose an agent
				</a>
			</div>
		</AccountManagementPanel>
	)
}

const compactGhostButtonCss = getGhostButtonCss({ size: 'sm' })

const primaryButtonCss = {
	...getPillButtonCss({ size: 'sm' }),
	textDecoration: 'none',
	width: 'fit-content',
}

const backLinkCss = {
	display: 'inline-flex',
	alignItems: 'center',
	gap: spacing.xs,
	width: 'fit-content',
	fontSize: typography.fontSize.sm,
	fontWeight: typography.fontWeight.medium,
	color: colors.primaryText,
	textDecoration: 'none',
	'&:hover': {
		color: colors.text,
	},
}
